import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { AppService } from '@Core/services/proprietary/app.service';
import { EditStatusType, StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import {
    AddFiling,
    DeleteNewFilings,
    NewDocument,
    PrepareRequest,
    ResetState,
    SetDefaultDrawerFolder,
    SetObjectState,
    UpdateDocument,
    UpdateFiling,
    UpdateFilingReferences
} from './document-object.actions';
import { DocumentObjectDefaults } from './document-object.defaults';

export interface DocumentObjectStateModel {
    currentDocID: string;
    entities: any;
    document: any;
    filing: any;
    defaultDrawerFolder: any;
}

const DocumentObjectStateDefaults: DocumentObjectStateModel = {
    currentDocID: null,
    entities: {},
    document: {},
    filing: {},
    defaultDrawerFolder: { File_Drawer: '?', File_Folder: '?' }
};

@State<DocumentObjectStateModel>({
    name: 'documentObjectState',
    defaults: DocumentObjectStateDefaults
})
@Injectable()
export class DocumentObjectState {
    constructor(private app: AppService, private helper: StateHelperService) { }

    @Selector()
    static entities(state: DocumentObjectStateModel) {
        let tempEntities = { ...state.entities };
        tempEntities.Document = state.document;
        tempEntities.Filing = state.filing;
        return tempEntities;
    }

    @Selector()
    static document(state: DocumentObjectStateModel) {
        return state.document[state.currentDocID] || null;
    }

    @Selector()
    static documentByTaskID(state: DocumentObjectStateModel) {
        return (docID: string) => {
            if (docID) {
                for (var i in state.document) {
                    if (state.document[i].Task_ID == docID) {
                        return state.document[i];
                    }
                }
            }
            return {};
        };
    }

    @Selector()
    static filings(state: DocumentObjectStateModel) {
        let result = [];
        for (var i in state.filing) {
            result.push(state.filing[i]);
        }
        return result;
    }

    @Selector()
    static filingReferance(state: DocumentObjectStateModel) {
        let result: any = {};
        for (var i in state.filing) {
            if (state.filing[i].Edit_Status === 'A') {
                if (state.filing[i].Contract) {
                    result.Contract = state.filing[i].Contract;
                }
                if (state.filing[i].Claim) {
                    result.Claim = state.filing[i].Claim;
                }
            }
        }
        return result;
    }

    @Action(UpdateDocument)
    updateDocument(sc: StateContext<DocumentObjectStateModel>, action: UpdateDocument) {
        const state = sc.getState();
        const docID = action.document.Doc_ID;
        if (!docID) {
            return;
        }
        const currentDoc = state.document[docID];
        let updateDoc: any = { ...action.document };
        let currentEditStatus = currentDoc.Edit_Status;
        if (!currentEditStatus && docID) {
            currentEditStatus = 'N';
        }
        this.helper.setItemChanged(updateDoc, currentEditStatus);

        sc.patchState({
            document: {
                ...state.document,
                [docID]: { ...currentDoc, ...updateDoc }
            }
        });
    }

    @Action(UpdateFiling)
    updateFiling(sc: StateContext<DocumentObjectStateModel>, action: UpdateFiling) {
        const state = sc.getState();
        let docLinkID = action.filing.DocLink_ID;
        let updateFiling: any = { ...action.filing };

        sc.patchState({
            filing: {
                ...state.filing,
                [docLinkID]: {
                    ...state.filing[docLinkID],
                    ...updateFiling
                }
            }
        });
    }

    @Action(AddFiling)
    addFiling(sc: StateContext<DocumentObjectStateModel>, action: AddFiling) {
        const state = sc.getState();
        let newFilingID: any = this.helper.getTempID();
        let newFiling = this.helper.createDefaultItem(DocumentObjectDefaults, 'Filing', action.filing);
        this.helper.setItemAdded(newFiling);
        newFiling.DocLink_ID = newFilingID;
        newFiling.File_Drawer = state?.defaultDrawerFolder?.File_Drawer || '?';
        newFiling.File_Folder = state?.defaultDrawerFolder?.File_Folder || '?';

        let document = { ...state.document[state.currentDocID] };
        if (!document.Filing) {
            document.Filing = [];
        }
        document.Filing = [...document.Filing, newFilingID];

        sc.patchState({
            filing: { ...state.filing, [newFilingID]: newFiling },
            document: { ...state.document, [state.currentDocID]: document }
        });
    }

    @Action(DeleteNewFilings)
    deleteNewFilings(sc: StateContext<DocumentObjectStateModel>, action: DeleteNewFilings) {
        const state = sc.getState();

        let document = { ...state.document[state.currentDocID] };
        if (!document.Filing) {
            document.Filing = [];
        }

        let filing = cloneDeep(state.filing);

        for (var i in filing) {
            if (filing[i].Edit_Status === 'A') {
                document.Filing = this.helper.removeArrayItemByValue(document.Filing, i);
                delete filing[i];
            }
        }

        sc.patchState({
            filing: filing,
            document: { ...state.document, [state.currentDocID]: document }
        });
    }

    @Action(UpdateFilingReferences)
    updateFilingReferences(sc: StateContext<DocumentObjectStateModel>, action: UpdateFilingReferences) {
        const state = sc.getState();
        let filing = cloneDeep(state.filing);

        for (var i in filing) {
            if (filing[i].Edit_Status === 'A') {
                filing[i] = { ...filing[i], ...action.filingTemplate };
            }
        }

        sc.patchState({
            filing: filing
        });
    }

    @Action(SetObjectState)
    setObjectState(sc: StateContext<DocumentObjectStateModel>, action: SetObjectState) {
        const state = sc.getState();

        //Find the first (and ONLY) document
        let docID = null;
        for (var i in action.entities.Document) {
            docID = i;
            break;
        }

        // copy references to raw normalized entities and then remove the entities that have thier own state
        let entities = { ...action.entities };
        delete entities.Document;
        delete entities.Filing;

        //HACK!! DocumentGet does not return Edit_Status on Filings.   Initialize to 'N'
        //HACK!! DocumentGet SDK Problem.  Role_Type was moved to be an attribute of Entity.  Previously a separate element.
        //       Remove the "legacy"  Role_Type element.
        for (var i in action.entities.Filing) {
            action.entities.Filing[i].Edit_Status = 'N';
            if (action.entities.Filing[i].Role_Type) {
                delete action.entities.Filing[i].Role_Type;
            }
        }

        //HACK!! DocumentGet does not return Edit_Status on document.   Initialize to 'N'
        action.entities.Document.Edit_Status = 'N';

        // Make sure that entities "selector" get entities from all substates.   if a new substate is added here then we need to add to the selector
        sc.setState({
            entities: entities,
            document: action.entities.Document, // better to put entities that can be changed in a root state element???
            currentDocID: docID,
            filing: action.entities.Filing,
            defaultDrawerFolder: { File_Drawer: '?', File_Folder: '?' }
        });
    }

    @Action(ResetState)
    resetState(sc: StateContext<DocumentObjectStateModel>, action: ResetState) {
        sc.setState({
            currentDocID: null,
            entities: {},
            document: {},
            filing: {},
            defaultDrawerFolder: { File_Drawer: '?', File_Folder: '?' }
        });
    }

    @Action(NewDocument)
    newDocument(sc: StateContext<DocumentObjectStateModel>, action: NewDocument) {
        const state = sc.getState();

        let docID = '-1';
        let document = { ...action.documentDefaults, Doc_ID: docID, Edit_Status: 'A' };

        sc.setState({
            currentDocID: docID,
            entities: {
                Root: {},
                Transaction: {},
                Request: {}
            },
            document: { [docID]: document },
            filing: {},
            defaultDrawerFolder: { File_Drawer: '?', File_Folder: '?' }
        });
    }

    @Action(PrepareRequest)
    prepareRequest(sc: StateContext<DocumentObjectStateModel>, action: PrepareRequest) {
        // prepare ConceptOneRequest
        // Note: The normalize process will create a Root, Reply, and Transaction entity with an ID of 0
        //       When preparing a process transaction a Root, Request, and Transaction entities will have an ID of 1
        //       (then we will denormalize the Root entity with ID = 1)

        const state = sc.getState();

        let newDocument = false;
        let docID = state.currentDocID;
        if (docID < '1') {
            newDocument = true;
        }

        let processRoot = {
            '?xml': { '@version': '1.0' },
            ConceptOneRequest: '1'
        };

        let processRequest = {
            DocumentProcess: '1',
            SDK_Version: this.app.dsSystemInfo.objectNode.Product_Version
        };

        let processTran = {
            View: 'Full',
            Book_Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID,
            Book_Loc_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID,
            Document: docID,
            ...action.tranOptions
        };

        let document = cloneDeep(state.document[docID]);

        if (newDocument == true) {
            document.Task_ID = null; //don't supply the task ID' on a create
        }

        sc.patchState({
            document: { ...state.document, [docID]: document },
            entities: {
                ...state.entities,
                Root: {
                    ...state.entities.Root,
                    [1]: processRoot
                },
                Request: {
                    ...state.entities.Request,
                    [1]: processRequest
                },
                Transaction: {
                    ...state.entities.Transaction,
                    [1]: processTran
                }
            }
        });
    }

    @Action(SetDefaultDrawerFolder)
    setDefaultDrawerFolder(sc: StateContext<DocumentObjectStateModel>, action: SetDefaultDrawerFolder) {
        const state = sc.getState();
        sc.patchState({
            defaultDrawerFolder: action.defaults
        });

        if (action.updateFilings) {
            for (var idx in state.filing) {
                if (state.filing[idx].Edit_Status == EditStatusType.Add) {
                    let updateFiling = cloneDeep(state.filing[idx]);
                    updateFiling.File_Drawer = action.defaults.File_Drawer;
                    updateFiling.File_Folder = action.defaults.File_Folder;

                    sc.dispatch(new UpdateFiling(updateFiling));
                }
            }
        }
    }
}
